/** Campaign detail capabilities and tracker-qualified static tag scope.
 * For Media Devoted | Created By Ciro Autiero */

import { matchTracker, trackerCan, type TrackerId } from '@trafficpulse/shared';

import { dispatchTracker } from '@/hooks/campaign-tracker';
import type { CampaignProvider } from '@/hooks/voluum/useVoluum';
import type { FlowOptimizerProvider } from '@/features/flow-optimizer/services/flow-optimizer-api';

/** The Flow Optimizer provider of a campaign's tracker; null where it has none. */
export function optimizerProviderOf(provider: CampaignProvider): FlowOptimizerProvider | null {
  const tracker = dispatchTracker(provider);
  return trackerCan(tracker, 'flowOptimizer') ? tracker : null;
}

/** The trackers whose campaigns carry a static tag (GET/PUT /flow-optimizer/campaign-tags/:provider/:id). */
export type CampaignTagProvider = TrackerId;

/** The static-tag provider of a campaign's tracker; null where the tag route does not serve it. */
export function staticTagProviderOf(provider: CampaignProvider): CampaignTagProvider | null {
  return matchTracker<CampaignTagProvider | null>(dispatchTracker(provider), {
    voluum: () => 'voluum',
    binom: () => 'binom',
    'tres-commas': () => trackerCan('tres-commas', 'automationGates') ? 'tres-commas' : null,
  });
}

/** Whether the Flows tab may edit the tracker's flow (weights, path / item toggles, Save). */
export function flowWritesAllowed(provider: CampaignProvider): boolean {
  return trackerCan(dispatchTracker(provider), 'flowWrites');
}

/** What the Flows tab offers next to the flow: the optimizer, snapshots only, or nothing. */
export type FlowActions = 'optimizer' | 'snapshots' | 'read-only';

export function flowActionsOf(provider: CampaignProvider): FlowActions {
  const tracker = dispatchTracker(provider);
  if (trackerCan(tracker, 'flowOptimizer')) return 'optimizer';
  return trackerCan(tracker, 'flowWrites') ? 'snapshots' : 'read-only';
}
